import React, { useState, useEffect, useRef } from 'react';
import '../App.css';
import { useNavigate, useParams } from 'react-router-dom';
import { getMessages, sendMessage, getMatches } from '../api';
import { io } from 'socket.io-client';

const SOCKET_URL = 'https://kami-production-02dd.up.railway.app';

function Chat() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user'));
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const roomId = [user?.id, id].sort().join('_');

  useEffect(() => {
    loadData();

    // Connect to socket
    console.log('Connecting to socket...');
    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket', 'polling']
    });
    
    socketRef.current.on('connect', () => {
      console.log('Socket connected!', socketRef.current.id);
      socketRef.current.emit('join_room', roomId);
      console.log('Joined room:', roomId);
    });

    socketRef.current.on('connect_error', (err) => {
      console.log('Socket error:', err.message);
    });

    // Listen for incoming messages
    socketRef.current.on('receive_message', (data) => {
      console.log('Received message:', data);
      setMessages(prev => [...prev, data]);
    });

    return () => {
      socketRef.current.disconnect();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadData = async () => {
    try {
      const matchRes = await getMatches();
      const matchedUser = matchRes.data.find(m => m.id === parseInt(id));
      setMatch(matchedUser);

      const msgRes = await getMessages(id);
      setMessages(msgRes.data);
    } catch (err) {
      console.error('Error loading chat:', err);
    }
    setLoading(false);
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const newMessage = {
      sender_id: user?.id,
      receiver_id: parseInt(id),
      content: input,
      created_at: new Date(),
      roomId
    };

    // Add to local messages immediately
    setMessages(prev => [...prev, newMessage]);

    // Send via socket to other user
    socketRef.current.emit('send_message', newMessage);

    // Save to database
    try {
      await sendMessage(parseInt(id), input);
    } catch (err) {
      console.error('Error saving message:', err);
    }

    setInput('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  if (loading) {
    return (
      <div className="container">
        <div className="hero">
          <p className="auth-eyebrow">✦ Kami Cupid</p>
          <h1 className="auth-title">Loading...</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-page">
      <div className="chat-header">
        <button className="back-btn" onClick={() => navigate('/matches')}>← Back</button>
        <div className="chat-header-info">
          <div className="chat-avatar">
            {match?.photo
              ? <img src={match.photo} alt={match.name} style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }} />
              : '👤'}
          </div>
          <div>
            <h3>{match?.name}, {match?.age}</h3>
            <p>🟢 Online</p>
          </div>
        </div>
      </div>

      <div className="chat-messages">
        {messages.length === 0 && (
          <p style={{ textAlign: 'center', color: 'var(--muted)', fontStyle: 'italic', marginTop: 40 }}>
            No messages yet! Say hello 👋
          </p>
        )}
        {messages.map((msg, index) => {
          const isMe = msg.sender_id === user?.id;
          return (
            <div key={index} className={`message-row ${isMe ? 'me' : ''}`}>
              {!isMe && <div className="msg-avatar">👤</div>}
              <div className="message-bubble-wrap">
                <div className={`message-bubble ${isMe ? 'bubble-me' : 'bubble-them'}`}>
                  {msg.content}
                </div>
                <span className="msg-time">
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-area">
        <input
          className="chat-input"
          placeholder="Type a message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <button className="send-btn" onClick={handleSend}>💘 Send</button>
      </div>
    </div>
  );
}

export default Chat;